import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSkeleton from "../../../../hooks/useSkeleton";

import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { TabView, TabPanel } from "primereact/tabview";

import "react-toastify/dist/ReactToastify.css";

import helperFormats from "../../../../utils/helperFormats";

import { useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import { creditIdState } from "../../../../states/creditIdState";

import PayDebt from "./payDebt";
import CreditCancellation from "./creditCancellation";
import ManualPayment from "./manualPayment";

const CreditsForm = ({ settings }) => {
  const [isLoading, setIsLoading] = useAtom(loadingState);

  const creditId = useAtom(creditIdState);

  const s = useSkeleton("abm");
  const { t } = useTranslation();

  const isEditMode = settings.formMode === "UPDATE";
  const isDeleteMode = settings.formMode === "DELETE";

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      openedDate: "",
      application: {
        amount: 0,
        installmentsAmount: 0,
        installmentsNumber: 0,
        annualRate: 0,
        annualDefaultRate: 0,
        administrationFeeRate: 0,
        beneficiary_Name: "",
        beneficiary_LastName: "",
        beneficiary_DocumentNumber: "",
      },
    },
  });

  let passwordMode = "HIDDEN"; // "UPDATE", "VISIBLE"

  const formModeConfig = {};

  if (isEditMode) {
    formModeConfig.SubmitButtonText = t("buttons.update");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.serverAction = async (data) => {
      await settings.CRUDService.updateEntity(data);
    };
  } else if (isDeleteMode) {
    formModeConfig.SubmitButtonText = t("buttons.delete");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.deleting")}...`;
    formModeConfig.serverAction = async () => {
      await settings.CRUDService.deleteEntity(creditId);
    };
  } else {
    formModeConfig.SubmitButtonText = t("buttons.create");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.creating")}...`;
    formModeConfig.serverAction = async (data) => {
      await settings.CRUDService.createEntity(data);
    };
  }

  useEffect(() => {
    if (isEditMode || isDeleteMode) {
      const fetchData = async () => {
        try {
          const data = await settings.CRUDService.getEntity(creditId);

          for (const key in data) {
            setValue(key, data[key]);
          }
        } catch (error) {
          console.error(t("forms.messages.operation.error"), error);
        }
      };

      fetchData();
    }
  }, [creditId, setValue, settings.formMode]);

  const handlePassword = (e) => {
    e.preventDefault();
    passwordMode = "VISIBLE";
  };

  const [isVisible, setIsVisible] = useState(passwordMode == "VISIBLE");

  const handleShow = (e) => {
    e.preventDefault();
    setIsVisible(!isVisible);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await formModeConfig.serverAction(data);

      settings.hideDialog();

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      settings.toast.current.show({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  return (
    <React.Fragment>
      <div className="flex flex-column">
        <div className={s.get()}>
          <div>
            <div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className={`${s.get("form")} ${settings.formMode !== "DELETE" ? "abm-create-form" : ""}`}
              >
                <div className="card flex justify-content-center">
                  <div className="w-full">
                    <h3>{t("entities.credits.singular")}</h3>
                    <Controller
                      name="openedDate"
                      control={control}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="openedDate">
                              {t("entities.credits.openedDate")}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.openedDate)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="openedDate"
                                value={
                                  helperFormats.isDate(field.value)
                                    ? helperFormats.formatDate(
                                      new Date(field.value)
                                    )
                                    : "Pendiente"
                                }
                                maxLength={100}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.amount"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.amount">
                              {t("entities.credits.application.amount")}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.amount)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputNumber
                                id="application.amount"
                                value={field.value}
                                mode="decimal"
                                locale="de-DE"
                                minFractionDigits={2}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.installmentsAmount"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.installmentsAmount">
                              {t(
                                "entities.credits.application.installmentsAmount"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.installmentsAmount)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputNumber
                                id="application.installmentsAmount"
                                value={field.value}
                                mode="decimal"
                                locale="de-DE"
                                minFractionDigits={2}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.installmentsNumber"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.installmentsNumber">
                              {t(
                                "entities.credits.application.installmentsNumber"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.installmentsNumber)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputNumber
                                id="application.installmentsNumber"
                                value={field.value}
                                mode="decimal"
                                locale="de-DE"
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.annualRate"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.annualRate">
                              {t("entities.credits.application.annualRate")}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.annualRate)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="application.annualRate"
                                value={`${field.value * 100}%`}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.annualDefaultRate"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.annualDefaultRate">
                              {t(
                                "entities.credits.application.annualDefaultRate"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.annualDefaultRate)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="application.annualDefaultRate"
                                value={`${field.value * 100}%`}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.administrationFeeRate"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.administrationFeeRate">
                              {t(
                                "entities.credits.application.administrationFeeRate"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(
                                errors.administrationFeeRate
                              )}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="application.administrationFeeRate"
                                value={`${field.value * 100}%`}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                  <Divider layout="vertical" className="flex pr-6" />
                  <div className="w-full">
                    <h3>{t("entities.beneficiaries.singular")}</h3>
                    <Controller
                      name="application.beneficiary_DocumentNumber"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.beneficiary_DocumentNumber">
                              {t(
                                "entities.credits.application.beneficiary_documentNumber"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(
                                errors.beneficiary_DocumentNumber
                              )}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="application.beneficiary_DocumentNumber"
                                value={field.value}
                                maxLength={100}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.beneficiary_Name"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.beneficiary_Name">
                              {t(
                                "entities.credits.application.beneficiary_name"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.beneficiary_Name)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="application.beneficiary_Name"
                                value={field.value}
                                maxLength={100}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.beneficiary_LastName"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.beneficiary_LastName">
                              {t(
                                "entities.credits.application.beneficiary_lastName"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.beneficiary_LastName)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="application.beneficiary_LastName"
                                value={field.value}
                                maxLength={100}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.beneficiary_Phone"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.beneficiary_Phone">
                              {t(
                                "entities.credits.application.beneficiary_phone"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.beneficiary_Phone)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="application.beneficiary_Phone"
                                value={field.value}
                                maxLength={100}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="application.beneficiary_Address"
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <div className={s.get("form.field")}>
                          <div className={s.get("form.field.label")}>
                            <label htmlFor="application.beneficiary_Address">
                              {t(
                                "entities.credits.application.beneficiary_address"
                              )}
                            </label>
                          </div>
                          <div className={s.get("form.field.value")}>
                            <div className={s.get("form.field.value.error")}>
                              {getFormErrorMessage(errors.beneficiary_Address)}
                            </div>
                            <div className={s.get("form.field.value.control")}>
                              <InputText
                                id="application.beneficiary_Address"
                                value={field.value}
                                maxLength={100}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </form>
            </div>
            <Divider className="flex p-4" />
            <div>
              <TabView>
                <TabPanel header="Header I">
                  <p className="m-0">
                    <PayDebt></PayDebt>
                  </p>
                </TabPanel>
                <TabPanel header="Header II">
                  <p className="m-0">
                    <CreditCancellation></CreditCancellation>
                  </p>
                </TabPanel>
                <TabPanel header="Header III">
                  <p className="m-0">
                    <ManualPayment></ManualPayment>
                  </p>
                </TabPanel>
              </TabView>
            </div>
          </div>
        </div>
        <div className="flex justify-content-between mt-6 mb-2">
          <Button
            label={t("buttons.cancel")}
            icon="pi pi-times"
            className="p-button-text btn-secondary-crud"
            onClick={() => settings.hideDialog("CANCEL")}
            type="button"
            disabled={isLoading}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreditsForm;
